/**
 * Settings — where the application is turned DOWN, not up.
 *
 * ── What it is for ───────────────────────────────────────────────────────────
 *
 * Everything this application can do is on by default. This page is the list of
 * it, and the place to switch off the parts a particular person does not use.
 *
 * It was built the other way round first — everything off, one switch at a time
 * — and that was wrong for a reason worth keeping written down: an owner
 * meeting the app for the first time got one navigation item and an empty page,
 * with no way to distinguish an application that starts closed from one that
 * was never finished. Discoverability is not the same as minimalism.
 *
 * What survives from that version is the part that was right: a switch EXPLAINS
 * itself. Each row carries two paragraphs — what it puts on screen, and when it
 * earns its place — rather than a three-word label nobody can act on. The copy
 * lives in `lib/features.ts` beside the key it describes, in both languages,
 * because a label that drifts from its switch is worse than no label.
 *
 * ── Why the switches are local and not a permission ──────────────────────────
 *
 * These are a view preference stored per browser. They decide what is DRAWN.
 * They decide nothing about what is allowed: the server refuses what the
 * account may not do regardless, and a switch whose feature needs a capability
 * this account lacks is shown disabled with the reason rather than hidden, so
 * "why can I not see that" has an answer on screen. A rule the UI enforces is
 * not enforced.
 */

import { useCallback } from 'react'
import { signOut, type User } from '../lib/auth'
import { useLocale, type Locale } from '../lib/i18n'
import { Link } from '../lib/route'
import Icon, { type IconName } from '../components/Icon'
import Account from '../components/Account'
import LanguageToggle from '../components/LanguageToggle'
import {
  applyPreset,
  featureCopy,
  FEATURES,
  setFeature,
  useFeatures,
  type Feature,
  type FeatureGroup,
} from '../lib/features'
import type { Health } from '../App'

/* ────────────────────────────────────────────────────────────────────────────
 * Copy
 * ────────────────────────────────────────────────────────────────────────── */

const EN = {
  title: 'Settings',
  eyebrow: 'Your application',
  lede: 'Everything this application can do is switched on. This page is where you turn off the parts you do not use \u2014 each one says what it does and when it earns its place.',

  introTitle: 'Nothing here is permanent',
  introBody:
    'Switching something off changes only what you see, on this device. Nothing is deleted, no data stops being collected, and turning a screen back on shows everything that happened while it was hidden.',

  presets: 'Quick start',
  presetEssentials: 'Just the essentials',
  presetEverything: 'Everything on',
  presetNothing: 'Everything off',
  countOn: 'on',
  countOf: 'of',

  groupScreens: 'Screens',
  groupScreensWhy:
    'Each of these is a place in the navigation. On a computer they appear across the top; on a phone, in the bar along the bottom.',
  groupFeed: 'On the hunt screen',
  groupFeedWhy: 'What sits above the listings when you open the app.',
  groupDetail: 'On a listing',
  groupDetailWhy:
    'What you see once you open one piece of land. These stack down the page in the order below, so turning off the ones you do not read is the fastest way to make a listing legible.',
  groupTools: 'Tools',
  groupToolsWhy: 'Not part of the daily loop. Useful occasionally, and in the way the rest of the time.',

  needs: 'Needs the',
  needsSuffix: 'permission, which this account does not have.',
  opens: 'Opens',

  account: 'Account',
  language: 'Language',
  signOut: 'Sign out',
  about: 'About this app',
  version: 'Version',
  environment: 'Environment',

  sourcesTitle: 'Where listings come from',
  sourcesBody:
    'Listings enter this application three ways, and all three start with a person: you paste a link, you share one into the app from another app, or you type what is in front of you. Nothing is fetched automatically. Facebook Marketplace, ClasificadosOnline, Craigslist, OfferUp and eBay are recognised so their links open and are labelled correctly, but the application never visits them on its own — Clasificados and the others explicitly refuse automated collection, and that refusal is honoured rather than worked around. See docs/SOURCE_COMPLIANCE.md.',
} as const

type CopyKey = keyof typeof EN

const ES: Record<CopyKey, string> = {
  title: 'Ajustes',
  eyebrow: 'Tu aplicación',
  lede: 'Todo lo que esta aplicación puede hacer está prendido. Esta página es donde apagas lo que no uses \u2014 cada cosa dice qué hace y cuándo vale la pena tenerla.',

  introTitle: 'Nada de esto es permanente',
  introBody:
    'Apagar algo cambia solo lo que ves, en este equipo. No se borra nada, no se deja de recoger ningún dato, y volver a prender una pantalla enseña todo lo que pasó mientras estuvo escondida.',

  presets: 'Empezar rápido',
  presetEssentials: 'Solo lo esencial',
  presetEverything: 'Todo prendido',
  presetNothing: 'Todo apagado',
  countOn: 'prendidos',
  countOf: 'de',

  groupScreens: 'Pantallas',
  groupScreensWhy:
    'Cada una es un sitio en la navegación. En computadora salen arriba; en el teléfono, en la barra de abajo.',
  groupFeed: 'En la pantalla de búsqueda',
  groupFeedWhy: 'Lo que aparece encima de los anuncios cuando abres la app.',
  groupDetail: 'En una finca',
  groupDetailWhy:
    'Lo que ves cuando abres un terreno. Se apilan hacia abajo en el orden de la lista, así que apagar los que no lees es la vía más rápida de dejar la ficha legible.',
  groupTools: 'Herramientas',
  groupToolsWhy: 'No son parte del día a día. Útiles de vez en cuando, y estorbo el resto del tiempo.',

  needs: 'Necesita el permiso',
  needsSuffix: ', que esta cuenta no tiene.',
  opens: 'Abre',

  account: 'Cuenta',
  language: 'Idioma',
  signOut: 'Salir',
  about: 'Sobre esta app',
  version: 'Versión',
  environment: 'Ambiente',

  sourcesTitle: 'De dónde salen los anuncios',
  sourcesBody:
    'Los anuncios entran a esta aplicación de tres formas, y las tres empiezan con una persona: pegas un enlace, lo compartes desde otra app, o escribes lo que tienes delante. Nada se busca solo. Facebook Marketplace, ClasificadosOnline, Craigslist, OfferUp y eBay se reconocen para que sus enlaces abran y queden bien etiquetados, pero la aplicación nunca los visita por su cuenta — Clasificados y los demás rechazan expresamente la recolección automática, y ese rechazo se respeta en vez de darle la vuelta. Ver docs/SOURCE_COMPLIANCE.md.',
}

const COPY: Record<Locale, Record<CopyKey, string>> = { en: EN, 'es-PR': ES }

const GROUPS: { id: FeatureGroup; title: CopyKey; why: CopyKey }[] = [
  { id: 'screens', title: 'groupScreens', why: 'groupScreensWhy' },
  { id: 'feed', title: 'groupFeed', why: 'groupFeedWhy' },
  { id: 'detail', title: 'groupDetail', why: 'groupDetailWhy' },
  { id: 'tools', title: 'groupTools', why: 'groupToolsWhy' },
]

/* ────────────────────────────────────────────────────────────────────────────
 * The screen
 * ────────────────────────────────────────────────────────────────────────── */

export default function Settings({
  user,
  health,
  onAuthChange,
}: {
  user: User
  health: Health | null
  onAuthChange: () => Promise<void>
}) {
  const locale = useLocale()
  const t = useCallback((key: CopyKey) => COPY[locale][key] ?? EN[key], [locale])
  const on = useFeatures()

  const total = FEATURES.length
  const lit = FEATURES.filter((f) => on(f.key)).length

  return (
    <div className="settings">
      <div className="settings-intro">
        <Icon name="info" size={20} />
        <p>
          <b>{t('introTitle')}</b>
          {t('introBody')}
        </p>
      </div>

      <div className="presets">
        <span>{t('presets')}</span>
        <button type="button" onClick={() => applyPreset('essentials')}>{t('presetEssentials')}</button>
        <button type="button" onClick={() => applyPreset('everything')}>{t('presetEverything')}</button>
        <button type="button" onClick={() => applyPreset('nothing')}>{t('presetNothing')}</button>
        <span style={{ marginLeft: 'auto' }}>
          {lit} {t('countOf')} {total} {t('countOn')}
        </span>
      </div>

      {GROUPS.map((group) => (
        <section className="settings-group" key={group.id}>
          <h2>{t(group.title)}</h2>
          <p>{t(group.why)}</p>
          {FEATURES.filter((f) => f.group === group.id).map((feature) => (
            <ToggleRow key={feature.key} feature={feature} on={on(feature.key)} user={user} t={t} locale={locale} />
          ))}
        </section>
      ))}

      {/* Where listings come from. It belongs on this page and not in a
          document, because "is it connected to Craigslist" is the first thing
          anybody asks and the honest answer changes how they use intake. */}
      <section className="settings-group">
        <h2>{t('sourcesTitle')}</h2>
        <p style={{ maxWidth: '68ch', lineHeight: 1.65 }}>{t('sourcesBody')}</p>
      </section>

      <section className="settings-group">
        <h2>{t('account')}</h2>
        <Account user={user} />
      </section>

      <section className="settings-group">
        <h2>{t('language')}</h2>
        <LanguageToggle />
      </section>

      <section className="settings-group">
        <h2>{t('about')}</h2>
        <p className="muted small" style={{ margin: '0 0 14px' }}>
          {health
            ? `${t('version')} ${health.version} · ${health.commit} · ${t('environment')}: ${health.environment}`
            : '—'}
        </p>
        <div className="me-actions">
          <button
            type="button"
            onClick={async () => {
              await signOut()
              await onAuthChange()
            }}
          >
            {t('signOut')}
          </button>
        </div>
      </section>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * One switch
 * ────────────────────────────────────────────────────────────────────────── */

function ToggleRow({
  feature,
  on,
  user,
  t,
  locale,
}: {
  feature: Feature
  on: boolean
  user: User
  t: (key: CopyKey) => string
  locale: Locale
}) {
  const copy = featureCopy(feature, locale)
  /*
   * Disabled rather than hidden. A missing row cannot be asked about, and
   * "why can I not see the admin console" is a support conversation the screen
   * can answer on its own. The server refuses the calls either way.
   */
  const allowed = !feature.capability || user.capabilities.includes(feature.capability)
  const id = `feature-${feature.key.replace(/\./g, '-')}`

  return (
    <div className={`toggle-row ${on ? 'is-on' : ''}`}>
      <span className="toggle-icon" aria-hidden="true">
        <Icon name={feature.icon as IconName} size={20} />
      </span>

      <div>
        <label htmlFor={id} className="toggle-name" style={{ display: 'block', cursor: allowed ? 'pointer' : 'default' }}>
          {copy.name}
        </label>
        <p className="toggle-what">{copy.what}</p>
        <p className="toggle-why">{copy.why}</p>
        {feature.path && (
          <p className="toggle-where">
            {t('opens')} {feature.path}
          </p>
        )}
        {!allowed && (
          <p className="toggle-locked">
            {t('needs')} <code>{feature.capability}</code> {t('needsSuffix')}
          </p>
        )}
      </div>

      <span className="switch">
        <input
          id={id}
          type="checkbox"
          role="switch"
          checked={on && allowed}
          disabled={!allowed}
          onChange={(event) => setFeature(feature.key, event.target.checked)}
        />
        <span aria-hidden="true" />
      </span>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * The dead end that is not one
 * ────────────────────────────────────────────────────────────────────────── */

const LOCKED_EN = {
  eyebrow: 'Switched off',
  title: 'This screen is turned off',
  body: 'Somebody switched this off in settings. Nothing is wrong and nothing was lost \u2014 turn it back on and everything that happened while it was hidden is still here.',
  turnOn: 'Turn it on and open it',
  allSettings: 'All settings',
} as const

const LOCKED_ES: Record<keyof typeof LOCKED_EN, string> = {
  eyebrow: 'Apagada',
  title: 'Esta pantalla está apagada',
  body: 'Alguien la apagó en los ajustes. No hay nada malo y no se perdió nada \u2014 préndela otra vez y todo lo que pasó mientras estuvo escondida sigue ahí.',
  turnOn: 'Prenderla y abrirla',
  allSettings: 'Todos los ajustes',
}

/**
 * What a route whose switch is off renders instead of a 404.
 *
 * A notification, a bookmark or a shared link can land on a screen that is
 * currently switched off, and a "not found" there is a lie that reads as a
 * broken application. This says which switch, why it is off, and throws it in
 * place — so the link the owner followed still works, one tap later.
 */
export function FeatureOff({ feature }: { feature: Feature }) {
  const locale = useLocale()
  const copy = featureCopy(feature, locale)
  const words = locale === 'es-PR' ? LOCKED_ES : LOCKED_EN

  return (
    <div className="locked-screen">
      <Icon name={feature.icon as IconName} size={30} />
      <p className="eyebrow" style={{ justifyContent: 'center', marginTop: 14 }}>
        {words.eyebrow}
      </p>
      <h2>{copy.name}</h2>
      <p>{words.body}</p>
      <p className="toggle-why">{copy.why}</p>
      <div className="row">
        <button type="button" className="cta" onClick={() => setFeature(feature.key, true)}>
          <Icon name="check" size={17} />
          {words.turnOn}
        </button>
        <Link to="/settings" className="me-link" style={{ minHeight: 44 }}>
          <Icon name="settings" size={18} />
          {words.allSettings}
        </Link>
      </div>
    </div>
  )
}
